/**
 * 計助（Keisuke）用 サンプルデータカタログ（文献情報・学習ポイント付き）
 * 実戦用（Practical）と精度検証用（Verification）の2本立て。
 * R / Prism / 原著論文と結果を照合し、計算の正しさを証明するために使用する。
 *
 * 【ダッシュボード「サンプルデータ」メニューと analysisType の対応】
 * - 左ペインの解析タイプ・ANOVAの種類に合うものだけを `standardDatasetMatchesCurrentAnalysis` で表示。
 * - `analysisType` は .keisuke 保存・互換のための主たる解析カテゴリ。
 * - `excludeFromTTestSampleList`: 主解析が t-test でも「2群比較の例」として不適切なものを
 *   一覧から除外（検量線・回帰デモなど）。データ自体は配列に残す。
 *
 * | id (略)              | analysisType   | ANOVAサブ | メモ |
 * |---------------------|----------------|-----------|------|
 * | mouseWeight, tutorialTTest | t-test | - | 2群比較の代表例 |
 * | regression_bca      | regression | - | 検量線（BCA） |
 * | tTest_paired          | t-test | - | 対応ありt検定 |
 * | studentSleep        | t-test | - | R sleep 検証 |
 * | anscombe            | t-test（表示除外） | - | 回帰・Anscombe。4系列 |
 * | qpcr_Livak          | qpcr | - | Livak ΔΔCt 検証 |
 * | cellGrowth, tutorial_anova, plantGrowth, fisherIris | anova | oneWay | 読込時は入力形式を列フォーム（群名1行目が入力済み）に合わせる |
 * | toothGrowth, twoway_viability | anova | twoWay | |
 * | theoph, rmWeight    | anova | repeatedMeasures | |
 * | finney×2, tcid50    | dose-response | - | |
 * | survival×3          | survival | - | |
 */

import type { DataRow } from '../../../stores/systems/keisuke/dashboardStore';
import type { AnalysisTypeKey } from '../../../stores/systems/keisuke/dashboardStore';
import type { AnalysisOptions } from '../../../stores/systems/keisuke/dashboardStore';

export type StandardDatasetCategory = 'tutorial' | 'verification';

export interface StandardDatasetEntry {
  id: string;
  nameKey: string;
  descriptionKey: string;
  /** 文献（省略可）。i18n key または未設定 */
  referenceKey?: string;
  /** 文献のハイパーリンク先 URL（省略可）。指定時は UI で文献をリンク表示する */
  referenceUrl?: string;
  /** 学習のポイント。i18n key または未設定 */
  learningPointKey?: string;
  category: StandardDatasetCategory;
  analysisType: AnalysisTypeKey;
  /**
   * true のとき、t検定を選んでもサンプル一覧に含めない。
   * 検量線・Anscombe（回帰デモ）など、`analysisType` が t-test でも「2群比較の教材例」として不適切なデータ向け。
   */
  excludeFromTTestSampleList?: boolean;
  suggestedOptions: Partial<AnalysisOptions>;
  columns: string[];
  data: DataRow[];
}

// ========== 実戦用（Tutorial / Practical） ==========

/** 実戦用: マウス体重比較（WT vs KO） */
const practicalMouseWeight: StandardDatasetEntry = {
  id: 'practical_mouseWeight',
  nameKey: 'keisuke.sampleData.tutorial.mouseWeight.name',
  descriptionKey: 'keisuke.sampleData.tutorial.mouseWeight.description',
  learningPointKey: 'keisuke.sampleData.tutorial.mouseWeight.learningPoint',
  category: 'tutorial',
  analysisType: 't-test',
  suggestedOptions: {
    type: 't-test',
    testType: 'unpaired',
    equalVariance: false,
    mapping: { groupColumn: 'Group', valueColumn: 'Weight' },
  },
  columns: ['Group', 'Weight'],
  data: [
    { Group: 'WT', Weight: 24.1 },
    { Group: 'WT', Weight: 25.3 },
    { Group: 'WT', Weight: 23.8 },
    { Group: 'WT', Weight: 25.0 },
    { Group: 'WT', Weight: 24.6 },
    { Group: 'WT', Weight: 23.2 },
    { Group: 'WT', Weight: 24.8 },
    { Group: 'KO', Weight: 20.2 },
    { Group: 'KO', Weight: 19.8 },
    { Group: 'KO', Weight: 21.1 },
    { Group: 'KO', Weight: 20.5 },
    { Group: 'KO', Weight: 19.9 },
    { Group: 'KO', Weight: 20.8 },
    { Group: 'KO', Weight: 20.3 },
  ],
};

/** 実戦用: 対応あり t 検定（前後の測定） */
const tutorialTTestPaired: StandardDatasetEntry = {
  id: 'tutorial_tTest_paired',
  nameKey: 'keisuke.sampleData.tutorial.tTestPaired.name',
  descriptionKey: 'keisuke.sampleData.tutorial.tTestPaired.description',
  learningPointKey: 'keisuke.sampleData.tutorial.tTestPaired.learningPoint',
  category: 'tutorial',
  analysisType: 't-test',
  suggestedOptions: {
    type: 't-test',
    testType: 'paired',
    mapping: { beforeColumn: 'Before', afterColumn: 'After' },
  },
  columns: ['Subject', 'Before', 'After'],
  data: [
    { Subject: 'S1', Before: 142, After: 128 },
    { Subject: 'S2', Before: 138, After: 125 },
    { Subject: 'S3', Before: 145, After: 130 },
    { Subject: 'S4', Before: 140, After: 127 },
    { Subject: 'S5', Before: 136, After: 124 },
    { Subject: 'S6', Before: 144, After: 129 },
  ],
};

/** 実戦用: 2群 t 検定（シンプル） */
const tutorialTTest: StandardDatasetEntry = {
  id: 'tutorial_tTest',
  nameKey: 'keisuke.sampleData.tutorial.tTest.name',
  descriptionKey: 'keisuke.sampleData.tutorial.tTest.description',
  learningPointKey: 'keisuke.sampleData.tutorial.tTest.learningPoint',
  category: 'tutorial',
  analysisType: 't-test',
  suggestedOptions: {
    type: 't-test',
    testType: 'unpaired',
    equalVariance: false,
    mapping: { groupColumn: 'group', valueColumn: 'value' },
  },
  columns: ['group', 'value'],
  data: [
    { group: 'A', value: 10 },
    { group: 'A', value: 12 },
    { group: 'A', value: 11 },
    { group: 'B', value: 14 },
    { group: 'B', value: 16 },
    { group: 'B', value: 15 },
  ],
};

/** 実戦用: 3種培地での細胞増殖（一元配置 ANOVA） */
const practicalCellGrowth: StandardDatasetEntry = {
  id: 'practical_cellGrowth',
  nameKey: 'keisuke.sampleData.tutorial.cellGrowth.name',
  descriptionKey: 'keisuke.sampleData.tutorial.cellGrowth.description',
  learningPointKey: 'keisuke.sampleData.tutorial.cellGrowth.learningPoint',
  category: 'tutorial',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'oneWay',
    postHoc: 'tukey',
    mapping: { groupColumn: 'Medium', factorAColumn: 'Medium', valueColumn: 'CellCount' },
  },
  columns: ['Medium', 'CellCount'],
  data: [
    { Medium: 'Control', CellCount: 1.2 },
    { Medium: 'Control', CellCount: 1.4 },
    { Medium: 'Control', CellCount: 1.1 },
    { Medium: 'Control', CellCount: 1.3 },
    { Medium: 'Control', CellCount: 1.25 },
    { Medium: 'New A', CellCount: 1.8 },
    { Medium: 'New A', CellCount: 1.9 },
    { Medium: 'New A', CellCount: 1.7 },
    { Medium: 'New A', CellCount: 1.85 },
    { Medium: 'New A', CellCount: 1.75 },
    { Medium: 'New B', CellCount: 2.1 },
    { Medium: 'New B', CellCount: 2.2 },
    { Medium: 'New B', CellCount: 2.0 },
    { Medium: 'New B', CellCount: 2.15 },
    { Medium: 'New B', CellCount: 2.05 },
  ],
};

/** チュートリアル用: 一元配置 ANOVA */
const tutorialAnova: StandardDatasetEntry = {
  id: 'tutorial_anova',
  nameKey: 'keisuke.sampleData.tutorial.anova.name',
  descriptionKey: 'keisuke.sampleData.tutorial.anova.description',
  learningPointKey: 'keisuke.sampleData.tutorial.anova.learningPoint',
  category: 'tutorial',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'oneWay',
    postHoc: 'tukey',
    mapping: { groupColumn: 'Group', factorAColumn: 'Group', valueColumn: 'Value' },
  },
  columns: ['Group', 'Value'],
  data: [
    { Group: 'Control', Value: 10.5 },
    { Group: 'Control', Value: 11.2 },
    { Group: 'Control', Value: 9.8 },
    { Group: 'Treatment A', Value: 15.2 },
    { Group: 'Treatment A', Value: 16.1 },
    { Group: 'Treatment A', Value: 14.8 },
    { Group: 'Treatment B', Value: 18.5 },
    { Group: 'Treatment B', Value: 19.2 },
    { Group: 'Treatment B', Value: 17.8 },
  ],
};

/** 実戦用: BCA検量線（濃度 vs 吸光度） */
const regressionPracticalBCA: StandardDatasetEntry = {
  id: 'regression_practical_bca',
  nameKey: 'keisuke.sampleData.tutorial.bcaCalibration.name',
  descriptionKey: 'keisuke.sampleData.tutorial.bcaCalibration.description',
  learningPointKey: 'keisuke.sampleData.tutorial.bcaCalibration.learningPoint',
  category: 'tutorial',
  analysisType: 'regression',
  suggestedOptions: {
    type: 'regression',
    regressionMode: 'calibration',
    regressionType: 'standard',
    mapping: { doseColumn: 'Concentration', valueColumn: 'Absorbance' },
  },
  columns: ['Concentration', 'Absorbance'],
  data: [
    { Concentration: 0, Absorbance: 0.021 },
    { Concentration: 125, Absorbance: 0.145 },
    { Concentration: 250, Absorbance: 0.268 },
    { Concentration: 500, Absorbance: 0.512 },
    { Concentration: 1000, Absorbance: 0.987 },
  ],
};

/** 実戦用: 抗がん剤投与マウスの生存期間（打ち切り含む） */
const practicalSurvivalAnticancer: StandardDatasetEntry = {
  id: 'practical_survivalAnticancer',
  nameKey: 'keisuke.sampleData.tutorial.survivalAnticancer.name',
  descriptionKey: 'keisuke.sampleData.tutorial.survivalAnticancer.description',
  learningPointKey: 'keisuke.sampleData.tutorial.survivalAnticancer.learningPoint',
  category: 'tutorial',
  analysisType: 'survival',
  suggestedOptions: {
    type: 'survival',
    mapping: {
      timeColumn: 'Time',
      eventColumn: 'Event',
      survivalGroupColumn: 'Group',
    },
  },
  columns: ['Group', 'Time', 'Event'],
  data: [
    { Group: 'Control', Time: 5, Event: 1 },
    { Group: 'Control', Time: 8, Event: 1 },
    { Group: 'Control', Time: 10, Event: 1 },
    { Group: 'Control', Time: 12, Event: 1 },
    { Group: 'Control', Time: 15, Event: 1 },
    { Group: 'Control', Time: 18, Event: 0 },
    { Group: 'Drug', Time: 20, Event: 1 },
    { Group: 'Drug', Time: 24, Event: 0 },
    { Group: 'Drug', Time: 28, Event: 0 },
    { Group: 'Drug', Time: 30, Event: 1 },
    { Group: 'Drug', Time: 35, Event: 0 },
    { Group: 'Drug', Time: 40, Event: 0 },
  ],
};

// ========== 精度検証用（Verification） ==========

/** Student's Sleep Data (1908) - t検定の原著。R の sleep データと一致（計 20 件・extra 値同一） */
const tTestStudentSleep: StandardDatasetEntry = {
  id: 'tTest_StudentSleep',
  nameKey: 'keisuke.sampleData.verification.studentSleep.name',
  descriptionKey: 'keisuke.sampleData.verification.studentSleep.description',
  referenceKey: 'keisuke.sampleData.verification.studentSleep.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/sleep.html',
  learningPointKey: 'keisuke.sampleData.verification.studentSleep.learningPoint',
  category: 'verification',
  analysisType: 't-test',
  suggestedOptions: {
    type: 't-test',
    testType: 'unpaired',
    equalVariance: false,
    mapping: { groupColumn: 'Group', valueColumn: 'ExtraSleep' },
  },
  columns: ['Group', 'ExtraSleep'],
  data: [
    { Group: 'Drug 1', ExtraSleep: 0.7 },
    { Group: 'Drug 1', ExtraSleep: -1.6 },
    { Group: 'Drug 1', ExtraSleep: -0.2 },
    { Group: 'Drug 1', ExtraSleep: -1.2 },
    { Group: 'Drug 1', ExtraSleep: -0.1 },
    { Group: 'Drug 1', ExtraSleep: 3.4 },
    { Group: 'Drug 1', ExtraSleep: 3.7 },
    { Group: 'Drug 1', ExtraSleep: 0.8 },
    { Group: 'Drug 1', ExtraSleep: 0.0 },
    { Group: 'Drug 1', ExtraSleep: 2.0 },
    { Group: 'Drug 2', ExtraSleep: 1.9 },
    { Group: 'Drug 2', ExtraSleep: 0.8 },
    { Group: 'Drug 2', ExtraSleep: 1.1 },
    { Group: 'Drug 2', ExtraSleep: 0.1 },
    { Group: 'Drug 2', ExtraSleep: -0.1 },
    { Group: 'Drug 2', ExtraSleep: 4.4 },
    { Group: 'Drug 2', ExtraSleep: 5.5 },
    { Group: 'Drug 2', ExtraSleep: 1.6 },
    { Group: 'Drug 2', ExtraSleep: 4.6 },
    { Group: 'Drug 2', ExtraSleep: 3.4 },
  ],
};

/** R 標準 PlantGrowth - 一元配置 ANOVA。R の PlantGrowth と一致（30 件・weight/group 同一） */
const anovaPlantGrowth: StandardDatasetEntry = {
  id: 'anova_PlantGrowth',
  nameKey: 'keisuke.sampleData.verification.plantGrowth.name',
  descriptionKey: 'keisuke.sampleData.verification.plantGrowth.description',
  referenceKey: 'keisuke.sampleData.verification.plantGrowth.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/PlantGrowth.html',
  learningPointKey: 'keisuke.sampleData.verification.plantGrowth.learningPoint',
  category: 'verification',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'oneWay',
    postHoc: 'tukey',
    mapping: { groupColumn: 'Group', factorAColumn: 'Group', valueColumn: 'Weight' },
  },
  columns: ['Group', 'Weight'],
  data: [
    { Group: 'ctrl', Weight: 4.17 },
    { Group: 'ctrl', Weight: 5.58 },
    { Group: 'ctrl', Weight: 5.18 },
    { Group: 'ctrl', Weight: 6.11 },
    { Group: 'ctrl', Weight: 4.5 },
    { Group: 'ctrl', Weight: 4.61 },
    { Group: 'ctrl', Weight: 5.17 },
    { Group: 'ctrl', Weight: 4.53 },
    { Group: 'ctrl', Weight: 5.33 },
    { Group: 'ctrl', Weight: 5.14 },
    { Group: 'trt1', Weight: 4.81 },
    { Group: 'trt1', Weight: 4.17 },
    { Group: 'trt1', Weight: 4.41 },
    { Group: 'trt1', Weight: 3.59 },
    { Group: 'trt1', Weight: 5.87 },
    { Group: 'trt1', Weight: 3.83 },
    { Group: 'trt1', Weight: 6.03 },
    { Group: 'trt1', Weight: 4.89 },
    { Group: 'trt1', Weight: 4.32 },
    { Group: 'trt1', Weight: 4.69 },
    { Group: 'trt2', Weight: 6.31 },
    { Group: 'trt2', Weight: 5.12 },
    { Group: 'trt2', Weight: 5.54 },
    { Group: 'trt2', Weight: 5.5 },
    { Group: 'trt2', Weight: 5.37 },
    { Group: 'trt2', Weight: 5.29 },
    { Group: 'trt2', Weight: 4.92 },
    { Group: 'trt2', Weight: 6.15 },
    { Group: 'trt2', Weight: 5.8 },
    { Group: 'trt2', Weight: 5.26 },
  ],
};

/** Fisher's Iris (1936) - がく片長で3種比較。R の iris の Sepal.Length と一致（15 件サブセット） */
const anovaFisherIris: StandardDatasetEntry = {
  id: 'anova_FisherIris',
  nameKey: 'keisuke.sampleData.verification.fisherIris.name',
  descriptionKey: 'keisuke.sampleData.verification.fisherIris.description',
  referenceKey: 'keisuke.sampleData.verification.fisherIris.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/iris.html',
  learningPointKey: 'keisuke.sampleData.verification.fisherIris.learningPoint',
  category: 'verification',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'oneWay',
    postHoc: 'tukey',
    mapping: { groupColumn: 'Species', factorAColumn: 'Species', valueColumn: 'SepalLength' },
  },
  columns: ['Species', 'SepalLength', 'SepalWidth', 'PetalLength', 'PetalWidth'],
  data: [
    { Species: 'setosa', SepalLength: 5.1, SepalWidth: 3.5, PetalLength: 1.4, PetalWidth: 0.2 },
    { Species: 'setosa', SepalLength: 4.9, SepalWidth: 3.0, PetalLength: 1.4, PetalWidth: 0.2 },
    { Species: 'setosa', SepalLength: 4.7, SepalWidth: 3.2, PetalLength: 1.3, PetalWidth: 0.2 },
    { Species: 'setosa', SepalLength: 4.6, SepalWidth: 3.1, PetalLength: 1.5, PetalWidth: 0.2 },
    { Species: 'setosa', SepalLength: 5.0, SepalWidth: 3.6, PetalLength: 1.4, PetalWidth: 0.2 },
    { Species: 'versicolor', SepalLength: 7.0, SepalWidth: 3.2, PetalLength: 4.7, PetalWidth: 1.4 },
    { Species: 'versicolor', SepalLength: 6.4, SepalWidth: 3.2, PetalLength: 4.5, PetalWidth: 1.5 },
    { Species: 'versicolor', SepalLength: 6.9, SepalWidth: 3.1, PetalLength: 4.9, PetalWidth: 1.5 },
    { Species: 'versicolor', SepalLength: 5.5, SepalWidth: 2.3, PetalLength: 4.0, PetalWidth: 1.3 },
    { Species: 'versicolor', SepalLength: 6.5, SepalWidth: 2.8, PetalLength: 4.6, PetalWidth: 1.5 },
    { Species: 'virginica', SepalLength: 6.3, SepalWidth: 3.3, PetalLength: 6.0, PetalWidth: 2.5 },
    { Species: 'virginica', SepalLength: 5.8, SepalWidth: 2.7, PetalLength: 5.1, PetalWidth: 1.9 },
    { Species: 'virginica', SepalLength: 7.1, SepalWidth: 3.0, PetalLength: 5.9, PetalWidth: 2.1 },
    { Species: 'virginica', SepalLength: 6.3, SepalWidth: 2.9, PetalLength: 5.6, PetalWidth: 1.8 },
    { Species: 'virginica', SepalLength: 6.5, SepalWidth: 3.0, PetalLength: 5.8, PetalWidth: 2.2 },
  ],
};

/** 二元配置ANOVA 検証用: ToothGrowth (R 標準, Crampton 1947)。R の ToothGrowth のサブセット（18 件）と一致 */
const twowayAnovaToothGrowth: StandardDatasetEntry = {
  id: 'twoway_anova_verification_toothgrowth',
  nameKey: 'keisuke.sampleData.verification.toothGrowth.name',
  descriptionKey: 'keisuke.sampleData.verification.toothGrowth.description',
  referenceKey: 'keisuke.sampleData.verification.toothGrowth.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/ToothGrowth.html',
  learningPointKey: 'keisuke.sampleData.verification.toothGrowth.learningPoint',
  category: 'verification',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'twoWay',
    mapping: { factorAColumn: 'Supp', factorBColumn: 'Dose', valueColumn: 'Length' },
  },
  columns: ['Supp', 'Dose', 'Length'],
  data: [
    { Supp: 'VC', Dose: 0.5, Length: 4.2 },
    { Supp: 'VC', Dose: 0.5, Length: 11.5 },
    { Supp: 'VC', Dose: 0.5, Length: 7.3 },
    { Supp: 'OJ', Dose: 0.5, Length: 15.2 },
    { Supp: 'OJ', Dose: 0.5, Length: 21.5 },
    { Supp: 'OJ', Dose: 0.5, Length: 17.6 },
    { Supp: 'VC', Dose: 1.0, Length: 16.5 },
    { Supp: 'VC', Dose: 1.0, Length: 15.2 },
    { Supp: 'VC', Dose: 1.0, Length: 17.3 },
    { Supp: 'OJ', Dose: 1.0, Length: 22.6 },
    { Supp: 'OJ', Dose: 1.0, Length: 23.3 },
    { Supp: 'OJ', Dose: 1.0, Length: 25.8 },
    { Supp: 'VC', Dose: 2.0, Length: 23.6 },
    { Supp: 'VC', Dose: 2.0, Length: 25.5 },
    { Supp: 'VC', Dose: 2.0, Length: 26.4 },
    { Supp: 'OJ', Dose: 2.0, Length: 25.5 },
    { Supp: 'OJ', Dose: 2.0, Length: 26.4 },
    { Supp: 'OJ', Dose: 2.0, Length: 24.8 },
  ],
};

/** 二元配置ANOVA 実戦用: 薬剤と低酸素が細胞生存率に与える影響 */
const twowayAnovaPracticalViability: StandardDatasetEntry = {
  id: 'twoway_anova_practical_viability',
  nameKey: 'keisuke.sampleData.tutorial.twowayViability.name',
  descriptionKey: 'keisuke.sampleData.tutorial.twowayViability.description',
  learningPointKey: 'keisuke.sampleData.tutorial.twowayViability.learningPoint',
  category: 'tutorial',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'twoWay',
    mapping: { factorAColumn: 'Treatment', factorBColumn: 'Condition', valueColumn: 'Viability' },
  },
  columns: ['Treatment', 'Condition', 'Viability'],
  data: [
    { Treatment: 'Vehicle', Condition: 'Normoxia', Viability: 100.2 },
    { Treatment: 'Vehicle', Condition: 'Normoxia', Viability: 98.5 },
    { Treatment: 'Vehicle', Condition: 'Normoxia', Viability: 101.3 },
    { Treatment: 'Drug', Condition: 'Normoxia', Viability: 95.4 },
    { Treatment: 'Drug', Condition: 'Normoxia', Viability: 94.1 },
    { Treatment: 'Drug', Condition: 'Normoxia', Viability: 97.2 },
    { Treatment: 'Vehicle', Condition: 'Hypoxia', Viability: 85.3 },
    { Treatment: 'Vehicle', Condition: 'Hypoxia', Viability: 82.1 },
    { Treatment: 'Vehicle', Condition: 'Hypoxia', Viability: 84.8 },
    { Treatment: 'Drug', Condition: 'Hypoxia', Viability: 42.5 },
    { Treatment: 'Drug', Condition: 'Hypoxia', Viability: 38.9 },
    { Treatment: 'Drug', Condition: 'Hypoxia', Viability: 45.1 },
  ],
};

/** Anscombe's Quartet (1973)。回帰・診断用。2群t検定の例としては不適切のためサンプル一覧では非表示 */
const regressionAnscombe: StandardDatasetEntry = {
  id: 'regression_Anscombe',
  nameKey: 'keisuke.sampleData.verification.anscombe.name',
  descriptionKey: 'keisuke.sampleData.verification.anscombe.description',
  referenceKey: 'keisuke.sampleData.verification.anscombe.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/anscombe.html',
  learningPointKey: 'keisuke.sampleData.verification.anscombe.learningPoint',
  category: 'verification',
  analysisType: 't-test',
  excludeFromTTestSampleList: true,
  suggestedOptions: {
    type: 't-test',
    testType: 'unpaired',
    equalVariance: false,
    mapping: { groupColumn: 'Dataset', valueColumn: 'y' },
  },
  columns: ['Dataset', 'x', 'y'],
  data: [
    { Dataset: 'I', x: 10.0, y: 8.04 },
    { Dataset: 'I', x: 8.0, y: 6.95 },
    { Dataset: 'I', x: 13.0, y: 7.58 },
    { Dataset: 'I', x: 9.0, y: 8.81 },
    { Dataset: 'I', x: 11.0, y: 8.33 },
    { Dataset: 'II', x: 10.0, y: 9.14 },
    { Dataset: 'II', x: 8.0, y: 8.14 },
    { Dataset: 'II', x: 13.0, y: 8.74 },
    { Dataset: 'II', x: 9.0, y: 8.77 },
    { Dataset: 'II', x: 11.0, y: 9.26 },
    { Dataset: 'III', x: 10.0, y: 7.46 },
    { Dataset: 'III', x: 8.0, y: 6.77 },
    { Dataset: 'III', x: 13.0, y: 12.74 },
    { Dataset: 'III', x: 9.0, y: 7.11 },
    { Dataset: 'III', x: 11.0, y: 7.81 },
    { Dataset: 'IV', x: 8.0, y: 6.58 },
    { Dataset: 'IV', x: 8.0, y: 5.76 },
    { Dataset: 'IV', x: 8.0, y: 7.71 },
    { Dataset: 'IV', x: 8.0, y: 8.84 },
    { Dataset: 'IV', x: 19.0, y: 12.5 },
  ],
};

/** NCCTG Lung Cancer - 生存曲線検証用 */
const survivalNCCTG: StandardDatasetEntry = {
  id: 'survival_NCCTG',
  nameKey: 'keisuke.sampleData.verification.ncctl.name',
  descriptionKey: 'keisuke.sampleData.verification.ncctl.description',
  learningPointKey: 'keisuke.sampleData.verification.ncctl.learningPoint',
  category: 'verification',
  analysisType: 'survival',
  suggestedOptions: {
    type: 'survival',
    mapping: {
      timeColumn: 'Time',
      eventColumn: 'Event',
      survivalGroupColumn: 'Sex',
    },
  },
  columns: ['Time', 'Event', 'Sex'],
  data: [
    { Time: 306, Event: 1, Sex: 'Male' },
    { Time: 455, Event: 1, Sex: 'Male' },
    { Time: 1010, Event: 0, Sex: 'Male' },
    { Time: 210, Event: 1, Sex: 'Male' },
    { Time: 883, Event: 1, Sex: 'Male' },
    { Time: 1022, Event: 0, Sex: 'Male' },
    { Time: 310, Event: 1, Sex: 'Female' },
    { Time: 361, Event: 1, Sex: 'Female' },
    { Time: 218, Event: 1, Sex: 'Female' },
    { Time: 166, Event: 1, Sex: 'Female' },
    { Time: 170, Event: 1, Sex: 'Female' },
    { Time: 654, Event: 1, Sex: 'Female' },
  ],
};

/** Freireich 6-MP 白血病治療データ (1963) - ログランク検定の古典。R survival 等で用いられる白血病データと一致 */
const survivalFreireich: StandardDatasetEntry = {
  id: 'survival_Freireich',
  nameKey: 'keisuke.sampleData.verification.freireich.name',
  descriptionKey: 'keisuke.sampleData.verification.freireich.description',
  referenceKey: 'keisuke.sampleData.verification.freireich.reference',
  referenceUrl: 'https://pubmed.ncbi.nlm.nih.gov/13944535/',
  learningPointKey: 'keisuke.sampleData.verification.freireich.learningPoint',
  category: 'verification',
  analysisType: 'survival',
  suggestedOptions: {
    type: 'survival',
    mapping: {
      timeColumn: 'Time',
      eventColumn: 'Event',
      survivalGroupColumn: 'Group',
    },
  },
  columns: ['Group', 'Time', 'Event'],
  data: [
    { Group: 'Placebo', Time: 1, Event: 1 },
    { Group: 'Placebo', Time: 1, Event: 1 },
    { Group: 'Placebo', Time: 2, Event: 1 },
    { Group: 'Placebo', Time: 2, Event: 1 },
    { Group: 'Placebo', Time: 3, Event: 1 },
    { Group: 'Placebo', Time: 4, Event: 1 },
    { Group: 'Placebo', Time: 4, Event: 1 },
    { Group: 'Placebo', Time: 5, Event: 1 },
    { Group: 'Placebo', Time: 5, Event: 1 },
    { Group: 'Placebo', Time: 8, Event: 1 },
    { Group: 'Placebo', Time: 8, Event: 1 },
    { Group: 'Placebo', Time: 8, Event: 1 },
    { Group: 'Placebo', Time: 8, Event: 1 },
    { Group: 'Placebo', Time: 11, Event: 1 },
    { Group: 'Placebo', Time: 11, Event: 1 },
    { Group: 'Placebo', Time: 12, Event: 1 },
    { Group: 'Placebo', Time: 12, Event: 1 },
    { Group: 'Placebo', Time: 15, Event: 1 },
    { Group: 'Placebo', Time: 17, Event: 1 },
    { Group: 'Placebo', Time: 22, Event: 1 },
    { Group: 'Placebo', Time: 23, Event: 1 },
    { Group: '6-MP', Time: 6, Event: 1 },
    { Group: '6-MP', Time: 6, Event: 1 },
    { Group: '6-MP', Time: 6, Event: 1 },
    { Group: '6-MP', Time: 6, Event: 1 },
    { Group: '6-MP', Time: 7, Event: 1 },
    { Group: '6-MP', Time: 10, Event: 1 },
    { Group: '6-MP', Time: 13, Event: 1 },
    { Group: '6-MP', Time: 16, Event: 1 },
    { Group: '6-MP', Time: 22, Event: 1 },
    { Group: '6-MP', Time: 23, Event: 1 },
    { Group: '6-MP', Time: 6, Event: 0 },
    { Group: '6-MP', Time: 9, Event: 0 },
    { Group: '6-MP', Time: 10, Event: 0 },
    { Group: '6-MP', Time: 11, Event: 0 },
    { Group: '6-MP', Time: 17, Event: 0 },
    { Group: '6-MP', Time: 19, Event: 0 },
    { Group: '6-MP', Time: 20, Event: 0 },
    { Group: '6-MP', Time: 25, Event: 0 },
    { Group: '6-MP', Time: 32, Event: 0 },
    { Group: '6-MP', Time: 32, Event: 0 },
    { Group: '6-MP', Time: 34, Event: 0 },
    { Group: '6-MP', Time: 35, Event: 0 },
  ],
};

/** 回帰 検証用: 直線性の良い標準曲線（濃度 vs 吸光度） */
const regressionVerificationStandardCurve: StandardDatasetEntry = {
  id: 'regression_verification_standardCurve',
  nameKey: 'keisuke.sampleData.verification.standardCurve.name',
  descriptionKey: 'keisuke.sampleData.verification.standardCurve.description',
  learningPointKey: 'keisuke.sampleData.verification.standardCurve.learningPoint',
  category: 'verification',
  analysisType: 'regression',
  suggestedOptions: {
    type: 'regression',
    regressionMode: 'scatter',
    regressionType: 'standard',
    mapping: { doseColumn: 'Concentration', valueColumn: 'Absorbance' },
  },
  columns: ['Concentration', 'Absorbance'],
  data: [
    { Concentration: 0, Absorbance: 0.05 },
    { Concentration: 2, Absorbance: 0.21 },
    { Concentration: 4, Absorbance: 0.39 },
    { Concentration: 6, Absorbance: 0.58 },
    { Concentration: 8, Absorbance: 0.76 },
  ],
};

/** 回帰・検量線ワークスペース用（Anscombe I のみ） */
const regressionTutorialAnscombe: StandardDatasetEntry = {
  id: 'regression_tutorial_anscombe',
  nameKey: 'keisuke.sampleData.verification.anscombe.name',
  descriptionKey: 'keisuke.sampleData.verification.anscombe.description',
  referenceKey: 'keisuke.sampleData.verification.anscombe.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/anscombe.html',
  learningPointKey: 'keisuke.sampleData.verification.anscombe.learningPoint',
  category: 'tutorial',
  analysisType: 'regression',
  suggestedOptions: {
    type: 'regression',
    regressionMode: 'scatter',
    regressionType: 'standard',
    mapping: { doseColumn: 'x', valueColumn: 'y' },
  },
  columns: ['x', 'y'],
  data: [
    { x: 10.0, y: 8.04 },
    { x: 8.0, y: 6.95 },
    { x: 13.0, y: 7.58 },
    { x: 9.0, y: 8.81 },
    { x: 11.0, y: 8.33 },
  ],
};

/** 棒グラフのみ（統計検定なし）検証用 */
const barChartVerificationEnzyme: StandardDatasetEntry = {
  id: 'bar_chart_verification_enzyme',
  nameKey: 'keisuke.sampleData.verification.enzymeActivity.name',
  descriptionKey: 'keisuke.sampleData.verification.enzymeActivity.description',
  referenceKey: 'keisuke.sampleData.verification.enzymeActivity.reference',
  learningPointKey: 'keisuke.sampleData.verification.enzymeActivity.learningPoint',
  category: 'verification',
  analysisType: 'bar-chart',
  suggestedOptions: {
    type: 'bar-chart',
    mapping: { groupColumn: 'Condition', valueColumn: 'OD600' },
  },
  columns: ['Condition', 'OD600'],
  data: [
    { Condition: 'Untreated', OD600: 0.12 },
    { Condition: 'Untreated', OD600: 0.11 },
    { Condition: 'Low dose', OD600: 0.28 },
    { Condition: 'Low dose', OD600: 0.31 },
    { Condition: 'High dose', OD600: 0.52 },
    { Condition: 'High dose', OD600: 0.49 },
  ],
};

/** 棒グラフのみ（統計検定なし）ワークスペース用 */
const barChartTutorial: StandardDatasetEntry = {
  id: 'bar_chart_tutorial',
  nameKey: 'keisuke.sampleData.tutorial.cellGrowth.name',
  descriptionKey: 'keisuke.sampleData.tutorial.cellGrowth.description',
  learningPointKey: 'keisuke.sampleData.tutorial.cellGrowth.learningPoint',
  category: 'tutorial',
  analysisType: 'bar-chart',
  suggestedOptions: {
    type: 'bar-chart',
    mapping: { groupColumn: 'Medium', valueColumn: 'CellCount' },
  },
  columns: ['Medium', 'CellCount'],
  data: [
    { Medium: 'Control', CellCount: 1.2 },
    { Medium: 'Control', CellCount: 1.4 },
    { Medium: 'New A', CellCount: 1.8 },
    { Medium: 'New A', CellCount: 1.9 },
    { Medium: 'New B', CellCount: 2.1 },
  ],
};

/** 基本統計量 検証用: R faithful eruptions（先頭8件） */
const basicStatsVerificationEruptions: StandardDatasetEntry = {
  id: 'basic_stats_verification_eruptions',
  nameKey: 'keisuke.sampleData.verification.eruptions.name',
  descriptionKey: 'keisuke.sampleData.verification.eruptions.description',
  referenceKey: 'keisuke.sampleData.verification.eruptions.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/faithful.html',
  learningPointKey: 'keisuke.sampleData.verification.eruptions.learningPoint',
  category: 'verification',
  analysisType: 'basic-stats',
  suggestedOptions: {
    type: 'basic-stats',
    mapping: { valueColumn: 'eruptions' },
  },
  columns: ['eruptions'],
  data: [
    { eruptions: 3.6 },
    { eruptions: 1.8 },
    { eruptions: 3.333 },
    { eruptions: 2.283 },
    { eruptions: 4.533 },
    { eruptions: 2.883 },
    { eruptions: 4.7 },
    { eruptions: 2.283 },
  ],
};

/** 基本統計量ワークスペース用 */
const basicStatsTutorial: StandardDatasetEntry = {
  id: 'basic_stats_tutorial',
  nameKey: 'keisuke.sampleData.tutorial.basicStats.name',
  descriptionKey: 'keisuke.sampleData.tutorial.basicStats.description',
  learningPointKey: 'keisuke.sampleData.tutorial.basicStats.learningPoint',
  category: 'tutorial',
  analysisType: 'basic-stats',
  suggestedOptions: {
    type: 'basic-stats',
    mapping: { valueColumn: 'value' },
  },
  columns: ['value'],
  data: [{ value: 12 }, { value: 15 }, { value: 14 }, { value: 18 }, { value: 16 }, { value: 20 }],
};

/** qPCR 検証用: ΔCt 法（複数サンプル・同一プレート） */
const qpcrVerificationDeltaCt: StandardDatasetEntry = {
  id: 'qpcr_verification_deltaCt',
  nameKey: 'keisuke.sampleData.verification.qpcrDeltaCt.name',
  descriptionKey: 'keisuke.sampleData.verification.qpcrDeltaCt.description',
  learningPointKey: 'keisuke.sampleData.verification.qpcrDeltaCt.learningPoint',
  category: 'verification',
  analysisType: 'qpcr',
  suggestedOptions: {
    type: 'qpcr',
    qpcrMethod: 'deltaCt',
    mapping: {
      groupColumn: 'Sample',
      beforeColumn: 'HousekeepingCt',
      valueColumn: 'TargetCt',
    },
  },
  columns: ['Sample', 'HousekeepingCt', 'TargetCt'],
  data: [
    { Sample: 'A1', HousekeepingCt: 18.2, TargetCt: 22.1 },
    { Sample: 'A2', HousekeepingCt: 18.0, TargetCt: 21.9 },
    { Sample: 'A3', HousekeepingCt: 18.3, TargetCt: 22.3 },
    { Sample: 'B1', HousekeepingCt: 18.1, TargetCt: 24.5 },
    { Sample: 'B2', HousekeepingCt: 18.4, TargetCt: 24.8 },
    { Sample: 'B3', HousekeepingCt: 17.9, TargetCt: 24.2 },
  ],
};

/** qPCR ワークスペース用（ΔΔCt: Control / Treatment サンプル名） */
const qpcrTutorialLivak: StandardDatasetEntry = {
  id: 'qpcr_tutorial_livak',
  nameKey: 'keisuke.sampleData.verification.livak.name',
  descriptionKey: 'keisuke.sampleData.verification.livak.description',
  referenceKey: 'keisuke.sampleData.verification.livak.reference',
  referenceUrl: 'https://doi.org/10.1006/meth.2001.1262',
  learningPointKey: 'keisuke.sampleData.verification.livak.learningPoint',
  category: 'tutorial',
  analysisType: 'qpcr',
  suggestedOptions: {
    type: 'qpcr',
    qpcrMethod: 'deltaDeltaCt',
    mapping: {
      groupColumn: 'Sample',
      beforeColumn: 'HousekeepingCt',
      valueColumn: 'TargetCt',
    },
  },
  columns: ['Sample', 'HousekeepingCt', 'TargetCt'],
  data: [
    { Sample: 'Control 1', HousekeepingCt: 15.21, TargetCt: 26.15 },
    { Sample: 'Control 2', HousekeepingCt: 15.35, TargetCt: 26.4 },
    { Sample: 'Control 3', HousekeepingCt: 15.1, TargetCt: 26.05 },
    { Sample: 'Treatment 1', HousekeepingCt: 15.01, TargetCt: 28.55 },
    { Sample: 'Treatment 2', HousekeepingCt: 15.22, TargetCt: 28.7 },
    { Sample: 'Treatment 3', HousekeepingCt: 14.95, TargetCt: 28.45 },
  ],
};

/** Livak & Schmittgen (2001) - qPCR ΔΔCt 検証（論文 Methods 25(4), 402-408） */
const qpcrLivak: StandardDatasetEntry = {
  id: 'qpcr_Livak',
  nameKey: 'keisuke.sampleData.verification.livak.name',
  descriptionKey: 'keisuke.sampleData.verification.livak.description',
  referenceKey: 'keisuke.sampleData.verification.livak.reference',
  referenceUrl: 'https://doi.org/10.1006/meth.2001.1262',
  learningPointKey: 'keisuke.sampleData.verification.livak.learningPoint',
  category: 'verification',
  analysisType: 'qpcr',
  suggestedOptions: {
    type: 'qpcr',
    qpcrMethod: 'deltaDeltaCt',
    mapping: {
      groupColumn: 'Sample',
      beforeColumn: 'HousekeepingCt',
      valueColumn: 'TargetCt',
    },
  },
  columns: ['Group', 'Sample', 'HousekeepingCt', 'TargetCt'],
  data: [
    { Group: 'Control', Sample: 'Brain 1', HousekeepingCt: 15.21, TargetCt: 26.15 },
    { Group: 'Control', Sample: 'Brain 2', HousekeepingCt: 15.35, TargetCt: 26.4 },
    { Group: 'Control', Sample: 'Brain 3', HousekeepingCt: 15.1, TargetCt: 26.05 },
    { Group: 'Treatment', Sample: 'Liver 1', HousekeepingCt: 15.01, TargetCt: 28.55 },
    { Group: 'Treatment', Sample: 'Liver 2', HousekeepingCt: 15.22, TargetCt: 28.7 },
    { Group: 'Treatment', Sample: 'Liver 3', HousekeepingCt: 14.95, TargetCt: 28.45 },
  ],
};

/** Finney プロビット分析データ（用量反応・LD50 検証用）。Finney (1971) の表に基づく標準データと一致 */
const doseResponseFinney: StandardDatasetEntry = {
  id: 'doseResponse_Finney',
  nameKey: 'keisuke.sampleData.verification.finney.name',
  descriptionKey: 'keisuke.sampleData.verification.finney.description',
  referenceKey: 'keisuke.sampleData.verification.finney.reference',
  referenceUrl: 'https://www.cambridge.org/core/books/probit-analysis/C73E1E81429540A52A4B4B2E734D2B0C',
  learningPointKey: 'keisuke.sampleData.verification.finney.learningPoint',
  category: 'verification',
  analysisType: 'dose-response',
  suggestedOptions: {
    type: 'dose-response',
    drType: 'ld50',
    drMethod: 'probit',
    mapping: { doseColumn: 'Dose', effectColumn: 'Deaths', totalColumn: 'Total' },
  },
  columns: ['Dose', 'Deaths', 'Total'],
  data: [
    { Dose: 1.69, Deaths: 6, Total: 59 },
    { Dose: 1.72, Deaths: 13, Total: 60 },
    { Dose: 1.75, Deaths: 18, Total: 62 },
    { Dose: 1.78, Deaths: 28, Total: 56 },
    { Dose: 1.81, Deaths: 52, Total: 63 },
    { Dose: 1.84, Deaths: 53, Total: 59 },
    { Dose: 1.87, Deaths: 61, Total: 62 },
  ],
};

// ========== 縦断的データ・反復測定ANOVA ==========

/** テオフィリン薬物動態（Theoph）- R 標準。R の Theoph データの先頭 3 被験者分と一致 */
const rmAnovaTheoph: StandardDatasetEntry = {
  id: 'rm_anova_verification_theoph',
  nameKey: 'keisuke.sampleData.verification.theoph.name',
  descriptionKey: 'keisuke.sampleData.verification.theoph.description',
  referenceKey: 'keisuke.sampleData.verification.theoph.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/Theoph.html',
  learningPointKey: 'keisuke.sampleData.verification.theoph.learningPoint',
  category: 'verification',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'repeatedMeasures',
    anovaRepeatedMethod: 'lmm',
    mapping: { subjectColumn: 'Subject', factorAColumn: 'Time', valueColumn: 'Concentration' },
  },
  columns: ['Subject', 'Time', 'Concentration'],
  data: [
    { Subject: '1', Time: 0.0, Concentration: 0.0 },
    { Subject: '1', Time: 0.25, Concentration: 2.84 },
    { Subject: '1', Time: 0.57, Concentration: 6.57 },
    { Subject: '1', Time: 1.12, Concentration: 10.5 },
    { Subject: '1', Time: 2.02, Concentration: 9.66 },
    { Subject: '2', Time: 0.0, Concentration: 0.0 },
    { Subject: '2', Time: 0.27, Concentration: 1.72 },
    { Subject: '2', Time: 0.52, Concentration: 7.91 },
    { Subject: '2', Time: 1.0, Concentration: 8.31 },
    { Subject: '2', Time: 1.92, Concentration: 8.33 },
    { Subject: '3', Time: 0.0, Concentration: 0.0 },
    { Subject: '3', Time: 0.27, Concentration: 4.14 },
    { Subject: '3', Time: 0.58, Concentration: 8.8 },
    { Subject: '3', Time: 1.09, Concentration: 8.2 },
    { Subject: '3', Time: 2.07, Concentration: 6.44 },
  ],
};

/** 縦断解析 検証用: Theoph（薬物動態・同一被験体の経時変化） */
const longitudinalVerificationTheoph: StandardDatasetEntry = {
  id: 'longitudinal_verification_theoph',
  nameKey: 'keisuke.sampleData.verification.theophLongitudinal.name',
  descriptionKey: 'keisuke.sampleData.verification.theophLongitudinal.description',
  referenceKey: 'keisuke.sampleData.verification.theoph.reference',
  referenceUrl: 'https://stat.ethz.ch/R-manual/R-devel/library/datasets/html/Theoph.html',
  learningPointKey: 'keisuke.sampleData.verification.theophLongitudinal.learningPoint',
  category: 'verification',
  analysisType: 'longitudinal',
  suggestedOptions: {
    type: 'longitudinal',
    mapping: {
      subjectColumn: 'Subject',
      groupColumn: 'Dose',
      timeColumn: 'Time',
      valueColumn: 'Concentration',
    },
  },
  columns: ['Subject', 'Dose', 'Time', 'Concentration'],
  data: [
    { Subject: '1', Dose: '4', Time: 0.0, Concentration: 0.0 },
    { Subject: '1', Dose: '4', Time: 0.25, Concentration: 2.84 },
    { Subject: '1', Dose: '4', Time: 0.57, Concentration: 6.57 },
    { Subject: '1', Dose: '4', Time: 1.12, Concentration: 10.5 },
    { Subject: '2', Dose: '4', Time: 0.0, Concentration: 0.0 },
    { Subject: '2', Dose: '4', Time: 0.27, Concentration: 1.72 },
    { Subject: '2', Dose: '4', Time: 0.52, Concentration: 7.91 },
    { Subject: '2', Dose: '4', Time: 1.0, Concentration: 8.31 },
    { Subject: '3', Dose: '5', Time: 0.0, Concentration: 0.0 },
    { Subject: '3', Dose: '5', Time: 0.27, Concentration: 4.14 },
    { Subject: '3', Dose: '5', Time: 0.58, Concentration: 8.8 },
    { Subject: '3', Dose: '5', Time: 1.09, Concentration: 8.2 },
  ],
};

/** 縦断解析ワークスペース用: マウス体重（群×時点） */
const longitudinalPracticalWeight: StandardDatasetEntry = {
  id: 'longitudinal_practical_weight',
  nameKey: 'keisuke.sampleData.tutorial.rmWeight.name',
  descriptionKey: 'keisuke.sampleData.tutorial.rmWeight.description',
  learningPointKey: 'keisuke.sampleData.tutorial.rmWeight.learningPoint',
  category: 'tutorial',
  analysisType: 'longitudinal',
  suggestedOptions: {
    type: 'longitudinal',
    mapping: {
      subjectColumn: 'SubjectID',
      groupColumn: 'Diet',
      timeColumn: 'Week',
      valueColumn: 'Weight',
    },
  },
  columns: ['SubjectID', 'Diet', 'Week', 'Weight'],
  data: [
    { SubjectID: 'M1', Diet: 'Normal', Week: 1, Weight: 20.1 },
    { SubjectID: 'M1', Diet: 'Normal', Week: 2, Weight: 21.5 },
    { SubjectID: 'M1', Diet: 'Normal', Week: 3, Weight: 22.8 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 1, Weight: 20.5 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 2, Weight: 24.1 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 3, Weight: 28.3 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 1, Weight: 19.8 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 2, Weight: 21.0 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 3, Weight: 22.2 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 1, Weight: 20.2 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 2, Weight: 23.5 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 3, Weight: 27.1 },
  ],
};

/** 実戦用: マウス体重推移（通常食 vs 高脂肪食）- 反復測定 */
const rmAnovaPracticalWeight: StandardDatasetEntry = {
  id: 'rm_anova_practical_weight',
  nameKey: 'keisuke.sampleData.tutorial.rmWeight.name',
  descriptionKey: 'keisuke.sampleData.tutorial.rmWeight.description',
  learningPointKey: 'keisuke.sampleData.tutorial.rmWeight.learningPoint',
  category: 'tutorial',
  analysisType: 'anova',
  suggestedOptions: {
    type: 'anova',
    anovaType: 'repeatedMeasures',
    postHoc: 'tukey',
    mapping: { subjectColumn: 'SubjectID', factorAColumn: 'Week', valueColumn: 'Weight' },
  },
  columns: ['SubjectID', 'Diet', 'Week', 'Weight'],
  data: [
    { SubjectID: 'M1', Diet: 'Normal', Week: 1, Weight: 20.1 },
    { SubjectID: 'M1', Diet: 'Normal', Week: 2, Weight: 21.5 },
    { SubjectID: 'M1', Diet: 'Normal', Week: 3, Weight: 22.8 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 1, Weight: 20.5 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 2, Weight: 24.1 },
    { SubjectID: 'M2', Diet: 'HighFat', Week: 3, Weight: 28.3 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 1, Weight: 19.8 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 2, Weight: 21.0 },
    { SubjectID: 'M3', Diet: 'Normal', Week: 3, Weight: 22.2 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 1, Weight: 20.2 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 2, Weight: 23.5 },
    { SubjectID: 'M4', Diet: 'HighFat', Week: 3, Weight: 27.1 },
  ],
};

/** Finney (1971) 別形式 - Dose, TotalSubjects, DeadSubjects。同上 Finney 文献の別表形式 */
const doseResponseFinneyAlt: StandardDatasetEntry = {
  id: 'dose_verification_finney',
  nameKey: 'keisuke.sampleData.verification.finneyAlt.name',
  descriptionKey: 'keisuke.sampleData.verification.finneyAlt.description',
  referenceKey: 'keisuke.sampleData.verification.finney.reference',
  referenceUrl: 'https://www.cambridge.org/core/books/probit-analysis/C73E1E81429540A52A4B4B2E734D2B0C',
  learningPointKey: 'keisuke.sampleData.verification.finney.learningPoint',
  category: 'verification',
  analysisType: 'dose-response',
  suggestedOptions: {
    type: 'dose-response',
    drType: 'ld50',
    drMethod: 'probit',
    mapping: { doseColumn: 'Dose', effectColumn: 'DeadSubjects', totalColumn: 'TotalSubjects' },
  },
  columns: ['Dose', 'TotalSubjects', 'DeadSubjects'],
  data: [
    { Dose: 10, TotalSubjects: 50, DeadSubjects: 6 },
    { Dose: 20, TotalSubjects: 48, DeadSubjects: 18 },
    { Dose: 30, TotalSubjects: 46, DeadSubjects: 34 },
    { Dose: 40, TotalSubjects: 49, DeadSubjects: 42 },
    { Dose: 50, TotalSubjects: 50, DeadSubjects: 47 },
  ],
};

/** 実戦用: TCID50（ウイルス力価・Reed-Muench） */
const dosePracticalTCID50: StandardDatasetEntry = {
  id: 'dose_practical_tcid50',
  nameKey: 'keisuke.sampleData.tutorial.tcid50.name',
  descriptionKey: 'keisuke.sampleData.tutorial.tcid50.description',
  learningPointKey: 'keisuke.sampleData.tutorial.tcid50.learningPoint',
  category: 'tutorial',
  analysisType: 'dose-response',
  suggestedOptions: {
    type: 'dose-response',
    drType: 'tcid50',
    drMethod: 'reed-muench',
    mapping: { doseColumn: 'Dilution', effectColumn: 'InfectedWells', totalColumn: 'TotalWells' },
  },
  columns: ['Dilution', 'TotalWells', 'InfectedWells'],
  data: [
    { Dilution: 1000, TotalWells: 8, InfectedWells: 8 },
    { Dilution: 10000, TotalWells: 8, InfectedWells: 8 },
    { Dilution: 100000, TotalWells: 8, InfectedWells: 6 },
    { Dilution: 1000000, TotalWells: 8, InfectedWells: 2 },
    { Dilution: 10000000, TotalWells: 8, InfectedWells: 0 },
  ],
};

/** 卵巣がん生存データ (Edmonson, 1979)。R survival::ovarian のサブセットと一致 */
const survivalOvarian: StandardDatasetEntry = {
  id: 'survival_verification_ovarian',
  nameKey: 'keisuke.sampleData.verification.ovarian.name',
  descriptionKey: 'keisuke.sampleData.verification.ovarian.description',
  referenceKey: 'keisuke.sampleData.verification.ovarian.reference',
  referenceUrl: 'https://cran.r-project.org/web/packages/survival/vignettes/survival.pdf',
  learningPointKey: 'keisuke.sampleData.verification.ovarian.learningPoint',
  category: 'verification',
  analysisType: 'survival',
  suggestedOptions: {
    type: 'survival',
    mapping: {
      timeColumn: 'SurvivalDays',
      eventColumn: 'EventStatus',
      survivalGroupColumn: 'Treatment',
    },
  },
  columns: ['Treatment', 'SurvivalDays', 'EventStatus'],
  data: [
    { Treatment: 'Cyclophosphamide', SurvivalDays: 59, EventStatus: 1 },
    { Treatment: 'Cyclophosphamide', SurvivalDays: 115, EventStatus: 1 },
    { Treatment: 'Cyclophosphamide', SurvivalDays: 156, EventStatus: 1 },
    { Treatment: 'Cyclo+Adriamycin', SurvivalDays: 353, EventStatus: 1 },
    { Treatment: 'Cyclo+Adriamycin', SurvivalDays: 365, EventStatus: 1 },
    { Treatment: 'Cyclo+Adriamycin', SurvivalDays: 1227, EventStatus: 0 },
  ],
};

export const KEISUKE_STANDARD_DATASETS: StandardDatasetEntry[] = [
  practicalMouseWeight,
  tutorialTTest,
  tutorialTTestPaired,
  practicalCellGrowth,
  tutorialAnova,
  regressionPracticalBCA,
  practicalSurvivalAnticancer,
  tTestStudentSleep,
  anovaPlantGrowth,
  anovaFisherIris,
  twowayAnovaToothGrowth,
  twowayAnovaPracticalViability,
  regressionAnscombe,
  survivalNCCTG,
  survivalFreireich,
  survivalOvarian,
  qpcrLivak,
  doseResponseFinney,
  doseResponseFinneyAlt,
  dosePracticalTCID50,
  rmAnovaTheoph,
  rmAnovaPracticalWeight,
  longitudinalPracticalWeight,
  longitudinalVerificationTheoph,
  regressionTutorialAnscombe,
  regressionVerificationStandardCurve,
  basicStatsTutorial,
  basicStatsVerificationEruptions,
  qpcrTutorialLivak,
  qpcrVerificationDeltaCt,
  barChartTutorial,
  barChartVerificationEnzyme,
];

export function getStandardDatasetsByCategory(): {
  tutorial: StandardDatasetEntry[];
  verification: StandardDatasetEntry[];
} {
  const tutorial = KEISUKE_STANDARD_DATASETS.filter((d) => d.category === 'tutorial');
  const verification = KEISUKE_STANDARD_DATASETS.filter((d) => d.category === 'verification');
  return { tutorial, verification };
}

export function getStandardDatasetById(id: string): StandardDatasetEntry | undefined {
  return KEISUKE_STANDARD_DATASETS.find((d) => d.id === id);
}

/**
 * 左ペインで選択中の解析（タイプ・ANOVAのサブタイプ・t検定の対応）に合うサンプルデータかどうか。
 * - t検定: `analysisType === 't-test'` かつ `testType`（対応なし／あり）が一致
 * - 1要因ANOVA: `anova` かつ `anovaType` が oneWay（未指定は oneWay）
 * - 2要因ANOVA: `anova` かつ `anovaType === 'twoWay'`
 * - 反復測定ANOVA: `anova` かつ `anovaType === 'repeatedMeasures'`
 * - 用量反応・生存: 各 `analysisType` が一致するもののみ
 *
 * `type` が未設定・空文字のときは t検定用として扱い、サンプルが0件にならないようにする。
 */
export function standardDatasetMatchesCurrentAnalysis(
  entry: StandardDatasetEntry,
  currentOptions: AnalysisOptions
): boolean {
  const raw = currentOptions.type;
  const ct = (raw === '' || raw == null ? 't-test' : raw) as AnalysisTypeKey;
  if (entry.analysisType !== ct) {
    return false;
  }
  if (ct === 't-test' && entry.excludeFromTTestSampleList) {
    return false;
  }
  if (ct === 't-test') {
    const entryTest = entry.suggestedOptions?.testType ?? 'unpaired';
    const curTest = currentOptions.testType ?? 'unpaired';
    if (entryTest !== curTest) return false;
  }
  if (ct === 'anova') {
    const entrySub = entry.suggestedOptions?.anovaType ?? 'oneWay';
    const curSub = currentOptions.anovaType ?? 'oneWay';
    return entrySub === curSub;
  }
  return true;
}
